import { faRightToBracket } from '@fortawesome/pro-regular-svg-icons'
import { getFriendlyName } from 'Utils/FriendlyName'
import { enqueueNotification } from 'Utils/Helpers'
import { EntityPicker, FullScreenContentModal, FullScreenModal } from 'V2Components'
import { useAppDetailsContext } from 'features/snowflake'
import { BundleCreateModal } from 'features/bundles'
import { createPacPolicy } from 'features/policy'
import { AccessRequestSubmission, createRef, useAccessCartProvider } from 'features/resources'
import { getObject, reduxApiClient } from 'infra'
import { createDataSelectorHook } from 'infra/redux'
import { clearCart } from 'infra/redux/reducers/slices/cartSlice'
import { pushToSlice } from 'infra/redux/sliceHandlers'
import _ from 'lodash'
import { SnackBar } from 'procyon-ui'
import React, { useState } from 'react'

/**
 * @typedef { { showEntitySlectionModal:boolean, accessRequestSubmissionModal: boolean } } Views
 *
 * @param {{
 * views: Views,
 * setViews: ((views:Views) => void),
 * }} param0
 * @returns
 */

const useSlices = createDataSelectorHook([
  'snowFlakeAccounts',
  'snowFlakeUsers',
  'userList',
  'groupList',
  'policyList'
])

function CreateApplicationPolicyFlow({ views, setViews }) {
  const { app } = useAppDetailsContext()
  const { cartItems, clearCartItems } = useAccessCartProvider()
  const [selectedEntities, setSelectedEntities] = useState([])
  const [showCreateBundleModal, setShowCreateBundleModal] = useState(false)
  const [requestName, setRequestName] = useState('')
  const [comments, setComments] = useState('')
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const [dateTimeError, setDateTimeError] = useState('')
  const { slices } = useSlices()

  const getEntities = () => {
    const usersList = []

    const filterCurrentAccount = _.find(slices.snowFlakeAccounts, {
      Application: { RefID: app.ObjectMeta.ID }
    })

    const filterUsers = slices.snowFlakeUsers.filter(
      (role) => role.Spec.SnowflakeAccount.RefID === filterCurrentAccount?.ObjectMeta?.ID
    )

    filterUsers.filter((user) => {
      const userList = slices.userList.filter((puser) => puser.Spec.EmailID === user.Spec.Email)
      usersList.push(...userList)
    })
    return usersList
  }

  const handleDismissClick = () => {
    clearCartItems()
    setSelectedEntities([])
    setRequestName('')
    setComments('')
    setStartDate(null)
    setEndDate(null)
    setDateTimeError('')
  }

  const handleViewsChange = (v) => {
    setViews({ ...views, ...v })
  }

  const handleEntityModalContinue = () => {
    handleViewsChange({ showEntitySlectionModal: false, accessRequestSubmissionModal: true })
  }

  const handleRsrcEditClick = () => {}

  const handleRequestSubmit = async (data) => {
    const policy = await handlePolicyCreate(data)
    // pushToSlice(policy)
    setSelectedEntities([])
    clearCart()
    handleViewsChange({ showEntitySlectionModal: false, accessRequestSubmissionModal: false })
    clearCartItems()
  }

  const afterSubmit = () => {}

  const onSubmissionCancel = () => {
    handleViewsChange({ showEntitySlectionModal: false, accessRequestSubmissionModal: false })
  }

  return (
    <div>
      {views.accessRequestSubmissionModal && (
        <FullScreenModal showModal>
          <AccessRequestSubmission
            onAddMoreResourcesClick={() =>
              handleViewsChange({ accessRequestSubmissionModal: false })
            }
            onRsrcEditClick={handleRsrcEditClick}
            loadingMessage={`Creating Application's Policy`}
            showRequestForUserGroupsSummary={true}
            requestForUserGroupsRefs={selectedEntities.map(createRef)}
            mainTitle='Policy'
            onSubmit={handleRequestSubmit}
            afterSubmit={afterSubmit}
            onCancel={onSubmissionCancel}
            onManageUserGroupsClick={() =>
              handleViewsChange({
                showEntitySlectionModal: true,
                accessRequestSubmissionModal: false
              })
            }
            requestName={requestName}
            setRequestName={setRequestName}
            comments={comments}
            setComments={setComments}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateTimeError={dateTimeError}
            setDateTimeError={setDateTimeError}
          />
        </FullScreenModal>
      )}

      {views.showEntitySlectionModal && (
        <FullScreenContentModal width='auto'>
          <EntityPicker
            title={'Select Users'}
            errorMessage={selectedEntities.length < 1 && 'Select At least one entity.'}
            entities={getEntities()}
            selectedEntities={selectedEntities}
            setSelectedEntities={setSelectedEntities}
            onCancel={() => {
              handleViewsChange({ showEntitySlectionModal: false })
            }}
            onContinue={handleEntityModalContinue}
          />
        </FullScreenContentModal>
      )}

      {cartItems?.length > 0 && (
        <div className='fixed bottom-4 right-9'>
          <SnackBar
            showDropDownButton
            onClickContinueButton={() => {
              handleViewsChange({ showEntitySlectionModal: true })
            }}
            leftIcon={faRightToBracket}
            menuItems={[
              {
                label: 'Create Bundle',
                onClick() {
                  setShowCreateBundleModal(true)
                }
              },
              {
                label: 'Dismiss',
                onClick: handleDismissClick,
                value: 'first-item'
              }
            ]}
            textMessage={`${cartItems?.length} Applications on Queue`}
            variant='primary'
          />
        </div>
      )}

      {showCreateBundleModal && (
        <BundleCreateModal
          resoucesWithRoles={cartItems}
          onCancel={() => setShowCreateBundleModal(false)}
          onSuccess={() => setShowCreateBundleModal(false)}
          reqType='Target'
        />
      )}
    </div>
  )
}

async function handlePolicyCreate({
  requestForUserGroupsRefs,
  selectedRsrcs,
  requestName,
  comments,
  startDate,
  endDate
}) {
  const data = {
    ObjectMeta: {
      Kind: 'PacPolicy',
      Name: getFriendlyName(),
      RdOwners: {
        ObjectRef: requestForUserGroupsRefs
      }
    },
    Spec: {
      Description: comments,
      ActionMap: {}
    },
    IssuedTo: {
      ObjectRef: requestForUserGroupsRefs
    },
    NotBefore: startDate,
    NotAfter: endDate,
    GivenName: requestName,
    Type: 'Snowflake',
    Creator: ''
  }

  data.Spec.ActionMap.Snowflake = { PolicyRule: [] }
  selectedRsrcs.forEach(({ Resource }) => {
    data.Spec.ActionMap.Snowflake.PolicyRule.push({
      ObjectRef: Resource
    })
  })

  const policy = await createPacPolicy(data)
  enqueueNotification('Policy successfully created!', 'info')
  return policy
}

export { CreateApplicationPolicyFlow }

function selectDispatch(arg0) {
  throw new Error('Function not implemented.')
}
